import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Cookie } from 'lucide-react';
import { useCookieConsent, CookieConsents } from '@/hooks/useCookieConsent';
import { useState } from 'react';

export const CookieConsentBanner = () => {
  const { hasAnswered, isLoading, saveConsents, acceptAll, rejectAll } = useCookieConsent();
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [customConsents, setCustomConsents] = useState<CookieConsents>({
    essential: true,
    analytics: false,
    marketing: false,
    functional: false
  });

  if (isLoading || hasAnswered) {
    return null;
  }

  const handleCustomSave = () => {
    saveConsents(customConsents);
    setIsCustomizing(false);
  };

  return (
    <>
      <Card className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-[90] p-4 shadow-lg border-2">
        <div className="flex items-start gap-3">
          <Cookie className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
          <div className="flex-1 space-y-3">
            <div>
              <h3 className="font-semibold mb-1">Gestion des cookies</h3>
              <p className="text-sm text-muted-foreground">
                Nous utilisons des cookies pour améliorer votre expérience et analyser notre trafic.
                Vous pouvez accepter tous les cookies ou personnaliser vos préférences.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2">
              <Button 
                onClick={acceptAll} 
                className="flex-1"
                size="sm"
              >
                Tout accepter
              </Button>
              <Button 
                onClick={rejectAll} 
                variant="outline" 
                className="flex-1"
                size="sm"
              >
                Tout refuser
              </Button>
              <Dialog open={isCustomizing} onOpenChange={setIsCustomizing}>
                <DialogTrigger asChild>
                  <Button variant="ghost" className="flex-1" size="sm">
                    Personnaliser
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Personnaliser les cookies</DialogTitle>
                    <DialogDescription>
                      Choisissez les types de cookies que vous souhaitez autoriser.
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-6 py-4">
                    {/* Essential Cookies */}
                    <div className="flex items-start justify-between gap-4 p-4 rounded-lg border">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="essential" className="font-semibold">
                            Cookies essentiels
                          </Label>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-muted">
                            Requis
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Nécessaires au fonctionnement du site. Ils permettent la navigation et l'utilisation des fonctionnalités de base.
                        </p>
                      </div>
                      <Switch
                        id="essential"
                        checked={true}
                        disabled
                      />
                    </div>

                    {/* Analytics Cookies */}
                    <div className="flex items-start justify-between gap-4 p-4 rounded-lg border">
                      <div className="flex-1">
                        <Label htmlFor="analytics" className="font-semibold">
                          Cookies analytiques
                        </Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Nous permettent de mesurer l'audience et d'améliorer nos services en comprenant comment vous utilisez notre site.
                        </p>
                      </div>
                      <Switch
                        id="analytics"
                        checked={customConsents.analytics}
                        onCheckedChange={(checked) => 
                          setCustomConsents(prev => ({ ...prev, analytics: checked }))
                        }
                      />
                    </div>

                    {/* Marketing Cookies */}
                    <div className="flex items-start justify-between gap-4 p-4 rounded-lg border">
                      <div className="flex-1">
                        <Label htmlFor="marketing" className="font-semibold">
                          Cookies marketing
                        </Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Utilisés pour afficher des publicités pertinentes et mesurer l'efficacité de nos campagnes.
                        </p>
                      </div>
                      <Switch
                        id="marketing"
                        checked={customConsents.marketing}
                        onCheckedChange={(checked) => 
                          setCustomConsents(prev => ({ ...prev, marketing: checked }))
                        }
                      />
                    </div>

                    {/* Functional Cookies */}
                    <div className="flex items-start justify-between gap-4 p-4 rounded-lg border">
                      <div className="flex-1">
                        <Label htmlFor="functional" className="font-semibold">
                          Cookies fonctionnels
                        </Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Permettent de mémoriser vos préférences (langue, région) et d'améliorer votre expérience.
                        </p>
                      </div>
                      <Switch
                        id="functional"
                        checked={customConsents.functional}
                        onCheckedChange={(checked) => 
                          setCustomConsents(prev => ({ ...prev, functional: checked }))
                        }
                      />
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      onClick={handleCustomSave} 
                      className="flex-1"
                    >
                      Enregistrer mes préférences
                    </Button>
                    <Button 
                      onClick={() => setIsCustomizing(false)} 
                      variant="outline"
                    >
                      Annuler
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </Card>
    </>
  );
};
